/**
 * sessionStorage key under which a pending invite intent is stored.
 * The value is a JSON-serialised `PendingInviteIntent`.  sessionStorage is
 * used (rather than localStorage) so the intent is automatically cleared when
 * the browser tab is closed.
 */
export const PENDING_INVITE_STORAGE_KEY = 'pendingInviteIntent';

/**
 * Validates that an invite code is exactly 8 lowercase hex characters — the
 * format produced by the `generate_group_invite` RPC (which uses a random
 * 4-byte hex string via PostgreSQL `encode(gen_random_bytes(4), 'hex')`).
 */
export const INVITE_CODE_REGEX = /^[a-f0-9]{8}$/;

/**
 * Maximum age (in milliseconds) of a stored invite intent before it is
 * discarded on the client.  20 minutes matches the typical sign-up flow
 * duration.  Note: this is a client-side safety check only; the server-side
 * `expires_at` on the `group_invites` row is the authoritative expiry.
 */
export const INVITE_INTENT_TTL_MS = 20 * 60 * 1000;

/** Shape of the object stored under `PENDING_INVITE_STORAGE_KEY` in sessionStorage. */
export type PendingInviteIntent = {
  /** The raw invite code extracted from the invite URL. */
  code: string;
  /** `Date.now()` at the time the intent was stored — used for TTL checks. */
  createdAt: number;
  /** Item ID from the invite URL query param — used for Path B contextual variant. */
  itemId?: string;
};

// Shared item option lists — used in GroupView and MyItems to avoid duplication.
import type { PowerSource, CirculationMode } from '@/hooks/useItems';

export const POWER_SOURCES: { value: PowerSource; label: string }[] = [
  { value: 'NONE', label: 'Hand / None' },
  { value: 'BATTERY', label: 'Battery' },
  { value: 'MAINS', label: 'Mains' },
];

export const CIRCULATION_MODES: { value: CirculationMode; label: string; description: string }[] = [
  {
    value: 'RETURN_TO_OWNER_REQUIRED',
    label: 'Return to Owner',
    description: 'This item must return to you before it can be loaned again.',
  },
  {
    value: 'FREE_CIRCULATION_ALLOWED',
    label: 'Free Circulation',
    description:
      'This item may be passed directly between group members without returning to you first.',
  },
];

/**
 * Returns the human-readable label for a power source value, falling back to the raw value if not found.
 * @param powerSource
 */
export const formatPowerSourceLabel = (powerSource: PowerSource): string =>
  POWER_SOURCES.find((opt) => opt.value === powerSource)?.label ?? powerSource;

/**
 * localStorage key under which the user's cookie consent decision is stored.
 * Values: 'accepted' | 'declined' | (absent = no decision yet).
 *
 * localStorage is used (rather than sessionStorage) so the decision persists
 * across browser sessions and the banner is not shown again after a choice.
 */
export const COOKIE_CONSENT_STORAGE_KEY = 'cookieConsent';


export const ADD_ITEMS_NUDGE_KEY = 'addItemsNudgeDismissals';
export const INVITE_STEP_SKIPPED_KEY = 'inviteStepSkipped';

/**
 * Returns the base URL for auth email redirects (sign-up confirmation,
 * password reset).  Uses `VITE_APP_URL` when set — required for Capacitor
 * builds where `window.location.origin` returns `capacitor://localhost`
 * (iOS) or `http://localhost` (Android), neither of which resolves to a
 * valid web page.  Falls back to `window.location.origin` for web.
 */
export const getAppUrl = (): string =>
  import.meta.env.VITE_APP_URL || window.location.origin;

/** App Store URL for iOS forced-update deep link. Replace <APP_STORE_ID> once listed. */
export const APP_STORE_URL_IOS = 'https://apps.apple.com/app/id<APP_STORE_ID>';

/** Play Store URL for Android forced-update deep link. */
export const APP_STORE_URL_ANDROID = 'https://play.google.com/store/apps/details?id=uk.co.shareshack';

/** Stripe price ID for the monthly paid plan (set in .env). */
export const STRIPE_MONTHLY_PRICE_ID = import.meta.env.VITE_STRIPE_MONTHLY_PRICE_ID;

/** Stripe price ID for the annual paid plan (set in .env). */
export const STRIPE_ANNUAL_PRICE_ID = import.meta.env.VITE_STRIPE_ANNUAL_PRICE_ID;

/** Display prices for upgrade UI (not fetched from Stripe at runtime). */
export const PLAN_DISPLAY = {
  monthly: { label: 'Monthly', price: '\u00a32/mo', priceId: STRIPE_MONTHLY_PRICE_ID },
  annual: { label: 'Annual', price: '\u00a320/yr', saveBadge: 'Save 17%', priceId: STRIPE_ANNUAL_PRICE_ID },
} as const;
